import React from 'react';
import { Box, Typography } from '@mui/material';

const EducationSection = () => (
  <Box
    sx={{
      marginTop: '2rem',
      backgroundColor: 'background.paper',
      padding: '2rem',
      borderRadius: '16px',
      boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
    }}
  >
    {/* Can I Trust This Tool? */}
    <Box sx={{ marginBottom: '2rem' }}>
      <Typography
        variant="h2"
        component="h2"
        sx={{ fontWeight: 'bold', marginBottom: '1rem', fontSize: '1.25rem' }}
      >
        Can I Trust This Tool?
      </Typography>
      <Typography variant="body1" sx={{ marginBottom: '1rem' }}>
        Yes! This tool is designed with zero-knowledge principles. Here’s what that means:
      </Typography>
      <ul>
        <li>
          <Typography variant="body1">
            Password generation happens entirely in your browser. No data is sent to a server.
          </Typography>
        </li>
        <li>
          <Typography variant="body1">
            Even we can't see or store your passwords – they remain private to you.
          </Typography>
        </li>
      </ul>
      <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
        Trust and Privacy: Your passwords are generated entirely in your browser, ensuring complete
        security and privacy. We never see or store your data.
      </Typography>
    </Box>

    {/* Why Secure Passwords Matter */}
    <Box sx={{ marginBottom: '2rem' }}>
      <Typography
        variant="h2"
        component="h2"
        sx={{ fontWeight: 'bold', marginBottom: '1rem', fontSize: '1.25rem' }}
      >
        Why Should You Care About Secure Passwords?
      </Typography>
      <Typography variant="body1" sx={{ marginBottom: '1rem' }}>
        Passwords are the keys to your online life. If a hacker gets access to one of your accounts, they
        can steal sensitive information, lock you out, or impersonate you. A weak password makes it easy
        for attackers to break in.
      </Typography>
      <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
        Example: In 2019, a breach exposed over 500 million email accounts because users reused weak
        passwords across multiple services.
      </Typography>
    </Box>

    {/* What Makes a Password Secure */}
    <Box sx={{ marginBottom: '2rem' }}>
      <Typography
        variant="h2"
        component="h2"
        sx={{ fontWeight: 'bold', marginBottom: '1rem', fontSize: '1.25rem' }}
      >
        What Makes a Password Secure?
      </Typography>
      <Typography variant="body1" sx={{ marginBottom: '1rem' }}>
        A strong password is one that is:
      </Typography>
      <ul>
        <li>
          <Typography variant="body1">
            Long – at least 16 characters, but the longer, the better.
          </Typography>
        </li>
        <li>
          <Typography variant="body1">
            Complex – includes uppercase, lowercase, numbers, and special symbols.
          </Typography>
        </li>
        <li>
          <Typography variant="body1">
            Unique – never reused across accounts.
          </Typography>
        </li>
      </ul>
      <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
        Tip: A strong password with high entropy can take billions of years to crack using brute force.
      </Typography>
    </Box>

    {/* Best Practices for Password Management */}
    <Box sx={{ marginBottom: '2rem' }}>
      <Typography
        variant="h2"
        component="h2"
        sx={{ fontWeight: 'bold', marginBottom: '1rem', fontSize: '1.25rem' }}
      >
        Best Practices for Managing Your Passwords
      </Typography>
      <ul>
        <li>
          <Typography variant="body1">
            Use a password manager to securely store and organise your passwords.
          </Typography>
        </li>
        <li>
          <Typography variant="body1">
            Enable two-factor authentication (2FA) wherever possible.
          </Typography>
        </li>
        <li>
          <Typography variant="body1">
            Never share your passwords with others, even friends or family.
          </Typography>
        </li>
        <li>
          <Typography variant="body1">
            Regularly update your passwords, especially for critical accounts.
          </Typography>
        </li>
      </ul>
      <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
        Pro Tip: Use different passwords for every account to minimise risks from breaches.
      </Typography>
    </Box>
  </Box>
);

export default EducationSection;
